import React, { useEffect, useRef, useState } from 'react';
import styles from './PerformTcso.module.css';
import buttonStyles from '../TcsoInventoryCheckScreen.module.css';
import inputStyles from '../TcsoDualFuelMeterDetailsScreen.module.css';
import Select, { SingleValue } from 'react-select';
import { TextInput } from '../../common/textInput/TextInput';
import { RadioInputAnswerOption, RadioInputQuestion } from '../../common/radioInputQuestion/RadioInputQuestion';
import BackButton from '../../common/button/BackButton';
import SmoothProgressBar from '../../common/progressBar/SmoothProgressBar';
import { formatGuid, getAuthTokenOrLogOut, TcsoReportingRequest, validateNewCommsHubGuids } from '../../util';
import { reportingClient } from '../../../api/atomProxyClient';
import { startChat } from '../../chat/chat-jq';
import { runTcso } from './tcsoRunner';

export interface PerformTcsoProps {
  requestBase: { identifier: string; chfDeviceId: string; gpfDeviceId: string };
  journeyId: string;
  chatReason: string;
  buildChatMessage: (details: { newChfId: string; newGpfId: string }) => string;
  onFinish: () => void;
  onPrevious: () => void;
  onBackToStart: () => void;
  onChatStarted: () => void;
}

type PerformTcsoPhase = 'FORM' | 'RUNNING' | 'SUCCESS' | 'ERROR';

const radioOptionsAerialInstalled: RadioInputAnswerOption[] = [
  {
    answerValue: 'Yes',
    inputName: 'Yes',
    radioBtnGroupName: 'aerial',
  },
  {
    answerValue: 'No',
    inputName: 'No',
    radioBtnGroupName: 'aerial',
  },
];

const locationOptions = [
  {
    value: 'Outside Premises',
    label: 'Outside Premises',
  },
  {
    value: 'Indoors on external wall',
    label: 'Indoors on external wall',
  },
  {
    value: 'Deep indoors',
    label: 'Deep indoors',
  },
  {
    value: 'Basement or Cellar',
    label: 'Basement or Cellar',
  },
  {
    value: 'Unknown',
    label: 'Unknown',
  },
];

/**
 * The reusable "Perform TCSO" section: New Comms Hub Details form -> TCSO run against
 * ATOM with a waiting screen -> success or catch-all error screen with SST chat handoff.
 * Owns its form/run state and reporting; the host journey supplies the site identifiers,
 * journey id, chat wiring and navigation callbacks.
 */
export default function PerformTcso({
  requestBase,
  journeyId,
  chatReason,
  buildChatMessage,
  onFinish,
  onPrevious,
  onBackToStart,
  onChatStarted,
}: PerformTcsoProps) {
  const [phase, setPhase] = useState<PerformTcsoPhase>('FORM');

  const [newChfId, setNewChfId] = useState('');
  const [newChfIdInputError, setNewChfIdInputError] = useState(false);
  const [newChfIdInputErrorMessage, setNewChfIdInputErrorMessage] = useState('');

  const [newGpfId, setNewGpfId] = useState('');
  const [newGpfIdInputError, setNewGpfIdInputError] = useState(false);
  const [newGpfIdInputErrorMessage, setNewGpfIdInputErrorMessage] = useState('');

  const [chfLocation, setChfLocation] = useState('');
  const [chfLocationInputError, setChfLocationInputError] = useState(false);
  const [chfLocationInputErrorMessage, setChfLocationInputErrorMessage] = useState('');

  const [aerialInstalled, setAerialInstalled] = useState('No');

  const abortControllerRef = useRef<AbortController | null>(null);
  const startingTimestampRef = useRef<Date>(new Date());

  useEffect(() => () => abortControllerRef.current?.abort(), []);

  const applyNewChfIdError = (error: string) => {
    setNewChfIdInputError(!!error);
    setNewChfIdInputErrorMessage(error);
  };

  const applyNewGpfIdError = (error: string) => {
    setNewGpfIdInputError(!!error);
    setNewGpfIdInputErrorMessage(error);
  };

  const validateChfLocation = (location: string) => {
    if (!location) {
      setChfLocationInputError(true);
      setChfLocationInputErrorMessage('Please select the comms hub location');
    } else {
      setChfLocationInputError(false);
      setChfLocationInputErrorMessage('');
    }
  };

  const newChfChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatGuid(event.target.value);
    const { chfError, gpfError } = validateNewCommsHubGuids(formattedValue, newGpfId);
    applyNewChfIdError(chfError);
    if (newGpfId) {
      applyNewGpfIdError(gpfError);
    }

    setNewChfId(formattedValue);
  };

  const newGpfChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatGuid(event.target.value);
    const { chfError, gpfError } = validateNewCommsHubGuids(newChfId, formattedValue);
    applyNewGpfIdError(gpfError);
    if (newChfId) {
      applyNewChfIdError(chfError);
    }

    setNewGpfId(formattedValue);
  };

  const chfLocationChangeHandler = (
    selectedChfLocation: SingleValue<{
      value: string;
      label: string;
    }>,
  ) => {
    validateChfLocation(selectedChfLocation?.value ?? '');

    setChfLocation(selectedChfLocation?.value ?? '');
  };

  const matchChfLocation = () => locationOptions.find((o) => o.value === chfLocation);

  const reportTcsoResult = (status: string) => {
    const now = new Date();

    const request: TcsoReportingRequest = {
      journeyId: journeyId,
      commsHubLocation: chfLocation,
      aerialInstalled: aerialInstalled.toUpperCase(),
      newChfId: newChfId,
      newGpfId: newGpfId,
      tcsoResultStatus: status,
      tcsoStartingTimeStamp: startingTimestampRef.current.toISOString(),
      tcsoResultTimeStamp: now.toISOString(),
      tcsoTimeTaken: now.getTime() - startingTimestampRef.current.getTime(),
    };

    reportingClient.tcso(request).then();
  };

  const performTcsoHandler = () => {
    const { chfError, gpfError } = validateNewCommsHubGuids(newChfId, newGpfId);
    applyNewChfIdError(chfError);
    applyNewGpfIdError(gpfError);
    validateChfLocation(chfLocation);
    if (chfError || gpfError || !chfLocation) {
      return;
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;
    startingTimestampRef.current = new Date();
    setPhase('RUNNING');

    void runTcso({
      ...requestBase,
      newChfId,
      newGpfId,
      chfLocation,
      aerialInstall: aerialInstalled.toUpperCase(),
      signal: controller.signal,
    }).then((result) => {
      if (controller.signal.aborted || result.outcome === 'CANCELLED') {
        return;
      }
      if (result.outcome === 'FIELD_ERRORS') {
        applyNewChfIdError(result.chfError);
        applyNewGpfIdError(result.gpfError);
        setPhase('FORM');
        return;
      }
      reportTcsoResult(result.outcome);
      setPhase(result.outcome === 'SUCCESS' ? 'SUCCESS' : 'ERROR');
    });
  };

  const startChatHandler = async () => {
    const { userEmail, token } = await getAuthTokenOrLogOut();
    startChat(buildChatMessage({ newChfId, newGpfId }), userEmail, token, requestBase.identifier, chatReason);
    onChatStarted();
  };

  return (
    <div className={`commsCheckDecommFlexContainer ${inputStyles.screenContainer}`}>
      {phase === 'RUNNING' && <SmoothProgressBar durationMinutes={5} />}
      <div className={'commsCheckDecommContent'}>
        <div className={'m-t-8'}>
          {phase !== 'RUNNING' && <BackButton onClick={onBackToStart} />}

          {phase === 'FORM' && (
            <>
              <div className={'bodyTitleText m-t-22'}>New Comms Hub Details</div>
              <div className={'bodyText'}>Enter the New Comms Hub Details to perform TCSO</div>

              <div className={'m-t-32'}>
                <TextInput
                  label={'What is the NEW GPF GUID?'}
                  inputName={'newGpfIdInput'}
                  inputValue={newGpfId}
                  onChangeHandler={newGpfChangeHandler}
                  showError={newGpfIdInputError}
                  errorMessage={newGpfIdInputErrorMessage}
                />
              </div>

              <div className={'m-t-32'}>
                <label className={`${styles.selectLabel} m-b-10`} htmlFor={'select-comms-hub-location-input'}>
                  What is the location of the comms hub?
                </label>
                <Select
                  id="select-comms-hub-location"
                  inputId="select-comms-hub-location-input"
                  placeholder="Select Comms Hub location"
                  className="basic-single"
                  classNamePrefix="select"
                  name="select-comms-hub-location"
                  isSearchable={true}
                  options={locationOptions}
                  onChange={chfLocationChangeHandler}
                  value={matchChfLocation()}
                />
                {chfLocationInputError && <span className="errorMessage">{chfLocationInputErrorMessage}</span>}
              </div>

              <div className={'m-t-22'}>
                <TextInput
                  label={'What is the NEW CHF GUID?'}
                  inputName={'newChfIdInput'}
                  inputValue={newChfId}
                  onChangeHandler={newChfChangeHandler}
                  showError={newChfIdInputError}
                  errorMessage={newChfIdInputErrorMessage}
                />
              </div>

              <div className={'m-t-8'}>
                <RadioInputQuestion
                  question={'Is there an Aerial installed for the new comms hub?'}
                  answers={radioOptionsAerialInstalled}
                  onChangeHandler={() => {
                    setAerialInstalled(aerialInstalled === 'Yes' ? 'No' : 'Yes');
                  }}
                  defaultValue={aerialInstalled}
                />
              </div>
            </>
          )}

          {phase === 'RUNNING' && (
            <div className={buttonStyles.waitingScreen}>
              <div className={'bodyTitleText'}>In progress</div>
              <div className={'bodyText'}>This may take 5 minutes, please wait.</div>
            </div>
          )}

          {phase === 'SUCCESS' && (
            <div className={buttonStyles.waitingScreen}>
              <div className={'bodyTitleText'}>TCSO completed</div>
              <div className={'bodyText'}>TCSO has been completed successfully</div>
            </div>
          )}

          {phase === 'ERROR' && (
            <div className={buttonStyles.waitingScreen}>
              <div className={'bodyTitleText'}>TCSO not completed</div>
              <div className={'bodyText'}>The TCSO couldn’t be completed.</div>
              <div className={'bodyText'}>Please contact SST</div>
            </div>
          )}
        </div>
      </div>

      <div className={`tcsoFooter ${inputStyles.footer}`}>
        {phase === 'FORM' && (
          <>
            <button
              className={`${buttonStyles.tcsoTimeoutButton} ${buttonStyles.bgcGreen} ${buttonStyles.mGB8}`}
              onClick={performTcsoHandler}
            >
              Perform TCSO
            </button>
            <button className={`${buttonStyles.tcsoTimeoutButton} ${buttonStyles.bgcDefault}`} onClick={onPrevious}>
              Previous
            </button>
          </>
        )}

        {phase === 'SUCCESS' && (
          <button className={`${buttonStyles.tcsoTimeoutButton} ${buttonStyles.bgcGreen}`} onClick={onFinish}>
            Finish
          </button>
        )}

        {phase === 'ERROR' && (
          <button className={`${buttonStyles.tcsoTimeoutButton} ${buttonStyles.bgcGreen}`} onClick={startChatHandler}>
            Start Chat
          </button>
        )}
      </div>
    </div>
  );
}
